import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { ApiRequestError } from '../../api/client.ts';
import { useLogin, useResendVerification } from '../../api/hooks/useAuth.ts';
import {
  AuthCard,
  buttonClass,
  Field,
  FormError,
  FormNotice,
  inputClass,
} from '../../components/AuthCard.tsx';

export default function Login() {
  const navigate = useNavigate();
  const login = useLogin();
  const resend = useResendVerification();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const notVerified =
    login.error instanceof ApiRequestError && login.error.code === 'email_not_verified';

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    login.mutate(
      { email, password },
      { onSuccess: () => void navigate('/plans', { replace: true }) },
    );
  }

  return (
    <AuthCard title="Sign in">
      <form onSubmit={onSubmit}>
        <FormError
          message={
            login.error
              ? notVerified
                ? 'Your email address is not verified yet.'
                : login.error.message
              : undefined
          }
        />
        {notVerified && (
          <FormNotice
            message={resend.isSuccess ? 'Verification mail sent — check your inbox.' : undefined}
          />
        )}
        <Field label="Email">
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>
        <Field label="Password">
          <input
            className={inputClass}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </Field>
        <button className={buttonClass} type="submit" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </button>
        {notVerified && (
          <button
            type="button"
            className="mt-3 w-full text-sm text-blue-600 hover:underline"
            onClick={() => resend.mutate(email)}
            disabled={resend.isPending}
          >
            Resend verification mail
          </button>
        )}
      </form>
      <p className="mt-4 text-center text-sm">
        <Link className="text-blue-600 hover:underline" to="/forgot-password">
          Forgotten your password?
        </Link>
      </p>
      <p className="mt-6 text-center text-sm text-gray-500">
        No account yet?{' '}
        <Link className="text-blue-600 hover:underline" to="/register">
          Register
        </Link>
      </p>
    </AuthCard>
  );
}
